import classNames from "classnames";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import maxBy from "lodash/maxBy";
import pickBy from "lodash/pickBy";
import { useActivityPermissions } from "PFApp/activities/hooks";
import { useCanEditAuditRoles } from "PFApp/activities/hooks/use_can_edit_audit_roles";
import { useActivityShowContext } from "PFApp/activities/show/context/activity_show_context";
import { useActivityShortlists } from "PFApp/activities/show/hooks/use_activity_shortlists";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { useActivityCompareStore } from "PFApp/activities/show/store/activity_compare";
import { useActivityTabsStore, useActivityTabsStoreActions } from "PFApp/activities/show/store/activity_tabs";
import { ActivityTab } from "PFApp/activities/types/activity_tab";
import { isAuditRoleDisabled } from "PFApp/audit_planner/parts/utils/helpers";
import { AnimatedContentSwitcher } from "PFApp/components/animated_content_switcher";
import { getGrowlId, useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Stack } from "PFComponents/containers";
import { Tabs } from "PFComponents/tabs";
import { Typography } from "PFComponents/typography";
import { useDateFormatter } from "PFCore/hooks";
import { ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";

import { useActivityPageCounters } from "../hooks/use_activity_page_counters";
import css from "./activity_page.module.scss";
import {
  ActivityEconomics,
  ActivityFeedback,
  ActivityInterested,
  ActivityKPIs,
  ActivityMatches,
  ActivityOverview,
  ActivityRoles,
  ActivityShortlist,
  ActivityVacancies,
  Header
} from "./parts";
import { ActivityKPIAlert } from "./parts/activity_kpi_alert";
import { ComparisonToast } from "./parts/comparison_toast";
import { ActivityHistoryTab } from "./parts/tabs/history";

export const ActivityPage = () => {
  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation(["activities", "auditPlanner"]);
  const { formatDateTime } = useDateFormatter();

  const activity = useCurrentActivity();
  const { isRole, isAudit } = useActivityShowContext();
  const { allShortlists } = useActivityShortlists();
  const compareIds = useActivityCompareStore((state) => state.compareIds);
  const tabs = useActivityTabsStore((state) => state.tabs);
  const tabActiveId = useActivityTabsStore((state) => state.tabActiveId);
  const { changeTab } = useActivityTabsStoreActions();
  const { clearComparisonToast } = useActivityCompareStore((state) => state.actions);
  const showTabs = tabs.length > 1;
  const { counters, setInterestsCount } = useActivityPageCounters();
  const { canSeeKPIs } = useActivityPermissions(activity);

  const { canEditAuditRole } = useCanEditAuditRoles({
    coowned: activity.coowned,
    templateId: activity.template_id,
    workflowState: activity.workflow_state
  });

  const tabsWithCounters = useMemo(
    () => tabs.map((tab) => ({ ...tab, count: counters[tab.id] })),
    [tabs, counters]
  );

  const isAuditRole = isAudit && isRole;
  const lastActionDate = maxBy(allShortlists || [], "updated_at")?.updatedAt;
  const showLastActionDate = tabActiveId === ActivityTab.Shortlist && (!isAuditRole || canEditAuditRole);

  const tabPages: Partial<Record<ActivityTab, ReactNode>> = useMemo(() => {
    const canViewTab = (tabId: string) => find(tabs, ["id", tabId]);

    const allPages = {
      [ActivityTab.Overview]: <ActivityOverview key={ActivityTab.Overview} />,
      [ActivityTab.Interested]: (
        <ActivityInterested
          key={ActivityTab.Interested}
          handleInterestsCountChange={(newCount) => setInterestsCount(newCount)}
        />
      ),
      [ActivityTab.Matches]: <ActivityMatches key={ActivityTab.Matches} />,
      [ActivityTab.Shortlist]: <ActivityShortlist key={ActivityTab.Shortlist} />,
      [ActivityTab.Roles]: <ActivityRoles key={ActivityTab.Roles} />,
      [ActivityTab.Vacancies]: <ActivityVacancies key={ActivityTab.Vacancies} />,
      [ActivityTab.Feedback]: <ActivityFeedback key={ActivityTab.Feedback} />,
      [ActivityTab.Economics]: <ActivityEconomics key={ActivityTab.Economics} />,
      [ActivityTab.KPI]: <ActivityKPIs key={ActivityTab.KPI} />,
      [ActivityTab.History]: <ActivityHistoryTab key={ActivityTab.History} />
    };

    return pickBy(allPages, (_, tabId) => canViewTab(tabId));
  }, [tabs, setInterestsCount]);

  const fadeHeader = !isEmpty(compareIds);

  const handleTabChange = useCallback(
    (tabId: ActivityTab) => {
      if (tabId !== ActivityTab.Shortlist && !isEmpty(compareIds)) {
        clearComparisonToast();
      }

      changeTab(tabId);
    },
    [changeTab, clearComparisonToast, compareIds]
  );

  if (isAuditRole && isAuditRoleDisabled(activity)) {
    showGrowl({
      id: getGrowlId("no-permission-or-activity-pending-alert-action", "alert"),
      kind: "alert",
      message: t("auditPlanner:parts.noPermissionOrActivityPending"),
      hidePreviousByTypeAndId: true,
      ttl: 5000
    });

    return <Navigate to="/audit_planner" replace />;
  }

  return (
    <div className={css.container} data-qa-id="ActivityPage.main">
      <Stack gap="spacingLg" className={css.page}>
        <Header className={classNames({ [css.fade]: fadeHeader })} />
        {canSeeKPIs && <ActivityKPIAlert engagementId={activity.id} />}
        <div className={classNames(css.toolbar, { [css.fade]: fadeHeader })}>
          {showTabs && (
            <Tabs
              onChange={handleTabChange}
              tabs={tabsWithCounters}
              activeTab={tabActiveId}
              qaId="ActivityPage.tabs"
            />
          )}
          {showLastActionDate && (
            <Typography variant="labelRegular" className={css.updatedAt}>{`${t(
              "activities:show.matches.lastEdited"
            )}: ${formatDateTime(lastActionDate)}`}</Typography>
          )}
        </div>
        <AnimatedContentSwitcher<ActivityTab>
          items={tabPages}
          active={tabActiveId || ActivityTab.Overview}
          className={css.content}
        />
        <ComparisonToast />
      </Stack>
    </div>
  );
};
